import { useCallback, useEffect, useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";

import { useQuery } from "@tanstack/react-query";
import {
  ArrowRightIcon,
  BanIcon,
  BookmarkIcon,
  CalendarIcon,
  CalendarXIcon,
  CheckIcon,
  ChevronDownIcon,
  ContactIcon,
  CornerLeftUpIcon,
  FileTextIcon,
  GripIcon,
  LayoutGridIcon,
  ListChecksIcon,
  ListIcon,
  MoveIcon,
  NetworkIcon,
  PinIcon,
  PlusIcon,
  SettingsIcon,
  TargetIcon,
  ThumbsUpIcon,
  Trash2Icon,
  TriangleAlertIcon,
  UserPlusIcon,
  UsersIcon,
} from "lucide-react";
import {
  type ChallengeIdeaFiltersState,
  useChallengeIdeaFiltersStore,
  useChallengeIdeaFilters,
} from "stores/challengeIdeaFilters";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { LabelChooser } from "simplydo/react/components/Choosers/LabelChooser/LabelChooser";
import { styled } from "simplydo/react/styled";
import { largeComputerUp, mobileOnly, useTheme } from "simplydo/react/theme";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import {
  Dropdown,
  DropdownItem,
  DropdownItemLink,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H2, H3, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import { List, ListItem } from "simplydo/react/ui/list";
import { Loader } from "simplydo/react/ui/loader";
import {
  Message,
  MessageActions,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import { Popup } from "simplydo/react/ui/popup";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import { useClearNotifications } from "utils/useClearNotifications";
import util from "utils/utils";

import { ChallengeChooser, UserChooser } from "components/lib/Choosers";
import ActionDropdown, {
  type Action,
} from "components/lib/ConfigurableTable/ActionDropdown";
// import IdeaListView from "components/lib/Ideas/IdeaView/IdeaListView/IdeaListView";
import { useTableSelection } from "components/lib/ConfigurableTable/useTableSelection";
import ConfirmAnnouncementModal from "components/lib/ConfirmAnnouncementModal";
import ChallengeIdeaFilters from "components/lib/Filters/ChallengeIdeaFilters";
import ChallengeIdeaFiltersButton from "components/lib/Filters/ChallengeIdeaFiltersButton";
import { GatedChallengeModal } from "components/lib/GatedChallengeModal";
import { GroupUserManagementModal } from "components/lib/GroupUserManagementModal";
import { confirmMerge } from "components/lib/IdeaMerge";
import {
  ManageIdeaStatusModal,
  type IdeaStatusCatalogueItem,
} from "components/lib/Ideas/AssignPublicStatusModal";
import { HandledIdeaPreview } from "components/lib/Ideas/IdeaPreview";
import IdeaCardView from "components/lib/Ideas/IdeaView/IdeaCardView";
import IdeaTableView from "components/lib/Ideas/IdeaView/IdeaTableView";
import PinIdeaHelper from "components/lib/Ideas/PinIdeaHelper";
import type { LibIdeaViewIdea } from "components/lib/Ideas/types";
import { useTrayStatus } from "components/lib/Notifications";
import ProjectBoard from "components/lib/ProjectBoard";
import BoardEvents from "components/lib/ProjectBoard/ProjectEvents/BoardEvents";
import { EmptyBox } from "components/lib/UI";

import { type ChallengeDetail, type ChallengeIdeaListItem } from "../../types";
import {
  IdeaDeletionModal,
  IdeaManagementDeadlineModal,
  IdeaMoveToLaneModal,
  Pagination,
} from "./IdeaActions";
import ChallengeMyIdeas from "./MyIdeas";
import { mergeBasicIdeasRefetch, useIdeaEnrichment } from "./useIdeaEnrichment";
import { useIdeaQueryParams } from "./useIdeaQueryParams";

const StyledIdeasContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledIdeaItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 100%;
  flex: 1;
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: ${({ theme }) =>
    theme.sizes.isMobile ? "column-reverse" : "row"};
  align-items: ${({ theme }) =>
    !theme.sizes.isMobile ? "flex-start" : "center"};
  justify-content: space-between;
  margin-bottom: 5px;
`;

const StyledFilters = styled.div`
  display: flex;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  align-items: center;
  gap: 10px;
  margin-bottom: 5px;

  ${mobileOnly} {
    width: 100%;
  }
  ${largeComputerUp} {
    margin-bottom: 0;
  }
`;

const IdeaViewTypes = [
  { key: "card", text: "Card", value: "card", icon: ContactIcon },
  { key: "list", text: "List", value: "list", icon: ListIcon },
  { key: "board", text: "Board", value: "board", icon: LayoutGridIcon },
];

type IdeasProps = { challenge: ChallengeDetail; newIdea: () => void };

const Ideas = ({ challenge, newIdea }: IdeasProps) => {
  const { t } = useTranslation();

  const location = useLocation();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const previewId = searchParams.get("idea");

  const [showMyIdeas, setShowMyIdeas] = useState(true);

  const [challengeChooserIdea, setChallengeChooserIdea] = useState<string[]>(
    [],
  );
  const ideaSelection = useTableSelection<ChallengeIdeaListItem>();
  const [assessmentAssignIdeas, setAssessmentAssignIdeas] = useState<string[]>(
    [],
  );
  const [projectAssignIdeas, setProjectAssignIdeas] = useState<string[]>([]);
  const [assessmentAssignRoundId, setAssessmentAssignRoundId] = useState("");
  const [nextPhaseChallenge, setNextPhaseChallenge] = useState<string[]>([]);
  const [deletingIdeas, setDeletingIdeas] = useState<ChallengeIdeaListItem[]>(
    [],
  );
  const [labelIdeas, setLabelIdeas] = useState<ChallengeIdeaListItem[]>([]);
  const [statusModal, setStatusModal] = useState<{
    ideaIds: string[];
    status: IdeaStatusCatalogueItem | null;
  } | null>(null);
  const [managementDeadlineIdeas, setManagementDeadlineIdeas] = useState<
    ChallengeIdeaListItem[]
  >([]);
  const [moveLaneIdeas, setMoveLaneIdeas] = useState<ChallengeIdeaListItem[]>(
    [],
  );
  const [groupAddIdeas, setGroupAddIdeas] = useState<ChallengeIdeaListItem[]>(
    [],
  );
  const [removingIdeaFromMerge, setRemovingIdeaFromMerge] = useState("");
  const [confirmAnnouncementModalOpen, setConfirmAnnouncementModalOpen] =
    useState(false);
  const [laneAnnouncements, setLaneAnnouncements] = useState<
    Array<OpenAPI.Schemas["Announcement"]>
  >([]);
  const [laneUpdateContext, setLaneUpdateContext] = useState<{
    ideaIds?: string[];
    laneId?: string;
    order?: number | null;
  }>({});

  const user = useCurrentUser();
  const hasUser = !!user;
  const ideaMergeEnabled =
    user?.ownerOrganisation?.enabledFeatures?.includes("ideaMerging");
  const canMakeIdea = util.canCreateIdea(user, challenge);

  const challengeId = challenge._id!;
  const shouldFetchIdeas = useMemo(
    () => !!challengeId && hasUser,
    [challengeId, hasUser],
  );

  const canManageChallenge = useMemo(
    () => util.canManageChallenge(user, challenge),
    [user, challenge],
  );
  const canActionIdeas = useMemo(
    () => util.canActionIdeas(user, challenge),
    [user, challenge],
  );
  const canViewProjectBoard = useMemo(
    () =>
      util.hasPermission(user, "challenge.viewProjectBoard", challenge._id) ||
      challenge.projectManagementVisibility === "users",
    [user, challenge],
  );

  const ideaFilters = useChallengeIdeaFilters();
  const { ideaViewType, filtersVisible } = ideaFilters;
  useClearNotifications("idea", previewId);
  useClearNotifications(
    "projectIdea",
    ideaViewType === "board" ? previewId : null,
  );

  const queryParams = useIdeaQueryParams(ideaViewType);

  const basicIdeaQuery = useQuery(
    initUseQuery(
      api.challenges.getIdeas,
      [challengeId!, { ...queryParams, contentType: "basic" }],
      {
        isEnabled: shouldFetchIdeas,
        structuralSharing: mergeBasicIdeasRefetch,
      },
    ),
  );

  useIdeaEnrichment(challengeId, queryParams, {
    enabled: shouldFetchIdeas && !!basicIdeaQuery.data,
    indexUpdatedAt: basicIdeaQuery.dataUpdatedAt,
  });

  const ideaFilterQuery = useQuery(
    initUseQuery(api.challenges.getIdeaFilters, [challengeId!], {
      isEnabled: shouldFetchIdeas,
    }),
  );

  const projectLaneQuery = useQuery(
    initUseQuery(api.boards.getProjectLanes, [challengeId!], {
      isEnabled: canManageChallenge || canViewProjectBoard,
    }),
  );

  const emptyIdeasResponse = useMemo(
    () =>
      ({
        ideas: [],
        nextPageAvailable: false,
        previousPageAvailable: false,
        total: 0,
        page: 1,
        ideasVotedFor: 0,
        invitations: [],
        laneImpactMeasures: {},
      }) as OpenAPI.GET<"/challenges/{id}/ideas">["response"],
    [],
  );

  const data = useMemo(() => {
    return basicIdeaQuery.data || emptyIdeasResponse;
  }, [basicIdeaQuery.data, emptyIdeasResponse]);

  const ideasLoading = basicIdeaQuery.isLoading;

  const setPreviewId = useCallback(
    (ideaId: string | null, options?: { toComments?: boolean }) => {
      setSearchParams((prev) => {
        if (ideaId) {
          prev.delete("focus");
          prev.set("idea", ideaId);
        } else {
          const highlight = prev.get("idea");
          if (highlight) {
            prev.set("focus", highlight);
          }
          prev.delete("idea");
        }
        return prev;
      });
      if (options?.toComments) {
        navigate(location.search + "#comments");
      }
    },
    [setSearchParams, location, navigate],
  );

  const [crossPageNavigating, setCrossPageNavigating] = useState(false);

  const ideaMap = useMemo(() => {
    const map: Record<string, ChallengeIdeaListItem> = {};
    data.ideas.forEach((idea) => {
      map[idea._id!] = idea;
    });
    return map;
  }, [data.ideas]);

  const selectedIdeas = useMemo(() => {
    return ideaSelection.items.map((i) => {
      const matchedIdea = ideaMap[i._id!];
      return matchedIdea || i;
    });
  }, [ideaMap, ideaSelection.items]);

  const [selectingMultipleCards, setSelectingMultipleCards] = useState(false);

  const theme = useTheme();

  const paramViewType = searchParams.get("queryView");

  // For project board view, we only want to paginate within the lane of the currently previewed idea
  const paginatedIdeas = useMemo(() => {
    if (ideaViewType !== "board") return data.ideas;
    if (!previewId) return data.ideas;
    const previewIdea = ideaMap[previewId];
    const currentLaneId = previewIdea?.projectManagement?.boards?.find(
      (b) => b.forId === challengeId,
    )?.lane;
    const ideasInLane = data.ideas.filter(
      (i) =>
        i.projectManagement?.boards?.find((b) => b.forId === challengeId)
          ?.lane === currentLaneId,
    );
    // Now sort by order in lane
    const sortedIdeasInLane = ideasInLane.sort((a, b) => {
      const aOrder = a.projectManagement?.boards?.find(
        (b) => b.forId === challengeId,
      )?.order;
      const bOrder = b.projectManagement?.boards?.find(
        (b) => b.forId === challengeId,
      )?.order;
      return (aOrder || 0) - (bOrder || 0);
    });
    return sortedIdeasInLane;
  }, [ideaViewType, data.ideas, ideaMap, previewId, challengeId]);

  const canGoPrevIdea = useMemo(
    () =>
      paginatedIdeas.findIndex((i) => i._id === previewId) > 0 ||
      data.previousPageAvailable,
    [paginatedIdeas, data.previousPageAvailable, previewId],
  );
  const canGoNextIdea = useMemo(
    () =>
      paginatedIdeas.findIndex((i) => i._id === previewId) !==
        paginatedIdeas.length - 1 || data.nextPageAvailable,
    [paginatedIdeas, data.nextPageAvailable, previewId],
  );

  useEffect(() => {
    // Pre-fetch the ideas for the previous and next ideas in the list
    if (!previewId) return;

    const ideaIndex = paginatedIdeas.findIndex((i) => i._id === previewId);
    if (ideaIndex === -1) return;

    for (const idea of [
      paginatedIdeas[ideaIndex - 1],
      paginatedIdeas[ideaIndex + 1],
    ]) {
      if (!idea?._id) continue;
      api.queryClient.ensureQueryData(initUseQuery(api.ideas.get, [idea._id]));
    }
  }, [paginatedIdeas, previewId]);

  const currentPreviewIdInList = useMemo(
    () => data.ideas.some((i) => i._id === previewId),
    [data.ideas, previewId],
  );
  const template = challenge.ownerIdeaTemplate;
  const assessmentRounds = useMemo(
    () => template?.assessments?.rounds || [],
    [template],
  );

  const updateFilters = useChallengeIdeaFiltersStore.setState;
  const updateSearchTerm = useCallback(
    (term: string) => {
      updateFilters({ query: term, page: 1 });
    },
    [updateFilters],
  );

  const setIdeaViewType = useCallback(
    (
      viewType: ChallengeIdeaFiltersState["ideaViewType"],
      resetParams = true,
    ) => {
      if (resetParams) {
        setSearchParams((prev) => {
          prev.delete("queryView");
          return prev;
        });
      }
      if (
        ["card", "list"].includes(viewType) &&
        util.localStorageIsSupported()
      ) {
        localStorage.setItem("ideaViewTypePreference", viewType);
      }

      useChallengeIdeaFiltersStore.getState().setIdeaViewType(viewType);
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (
      paramViewType &&
      paramViewType !== ideaViewType &&
      IdeaViewTypes.map((v) => v.value).includes(paramViewType)
    ) {
      setIdeaViewType(
        paramViewType as ChallengeIdeaFiltersState["ideaViewType"],
        false,
      );
    }
  }, [paramViewType, ideaViewType, setIdeaViewType]);

  useEffect(() => {
    if (ideaViewType !== "board") {
      setSelectingMultipleCards(false);
    }
  }, [ideaViewType]);

  const fetchIdeasPage = useCallback(
    (targetPage: number) => {
      return api.queryClient.fetchQuery(
        initUseQuery(
          api.challenges.getIdeas,
          [
            challengeId!,
            { ...queryParams, page: targetPage, contentType: "basic" },
          ],
          { structuralSharing: mergeBasicIdeasRefetch },
        ),
      );
    },
    [challengeId, queryParams],
  );

  const movePreviewIdea = useCallback(
    async (amount: number) => {
      const ideaIndex = paginatedIdeas.findIndex((i) => i._id === previewId);
      if (ideaIndex === -1) return;
      const newIndex = ideaIndex + amount;

      if (newIndex === -1 && data.previousPageAvailable) {
        const targetPage = ideaFilters.page - 1;
        setCrossPageNavigating(true);
        try {
          const response = await fetchIdeasPage(targetPage);
          updateFilters({ page: targetPage });
          const lastIdea = response.ideas[response.ideas.length - 1];
          if (lastIdea?._id) setPreviewId(lastIdea._id);
        } finally {
          setCrossPageNavigating(false);
        }
      } else if (newIndex === paginatedIdeas.length && data.nextPageAvailable) {
        const targetPage = ideaFilters.page + 1;
        setCrossPageNavigating(true);
        try {
          const response = await fetchIdeasPage(targetPage);
          updateFilters({ page: targetPage });
          const firstIdea = response.ideas[0];
          if (firstIdea?._id) setPreviewId(firstIdea._id);
        } finally {
          setCrossPageNavigating(false);
        }
      } else {
        const newPreviewId = paginatedIdeas[newIndex]?._id;
        if (newPreviewId) setPreviewId(newPreviewId);
      }
    },
    [
      data.nextPageAvailable,
      data.previousPageAvailable,
      fetchIdeasPage,
      ideaFilters.page,
      paginatedIdeas,
      previewId,
      setPreviewId,
      updateFilters,
    ],
  );

  const onSelectIdea = (idea: ChallengeIdeaListItem) =>
    ideaSelection.toggle(idea, !ideaSelection.isSelected(idea));

  const bulkUpdateStatus = useCallback(
    (
      selectedIdeaIds: string[],
      statusKey: string,
      status: boolean | string | null,
      extra: { message?: string; notifyAuthors?: boolean } = {},
    ) => {
      const performUpdate = () => {
        api.challenges.updateIdeasStatus(
          challengeId,
          { ideas: selectedIdeaIds, [statusKey]: status, ...extra },
          ({ ideas: updatedIdeas }) => {
            toast(`${selectedIdeaIds.length} ${t("generic.ideas")} updated`);
            api.setQueryData(
              api.challenges.getIdeas,
              [challengeId],
              (prev) => ({
                ...prev,
                ideas: prev.ideas.map((idea) => {
                  const updatedIdea = updatedIdeas.find(
                    (u) => u._id === idea._id,
                  );
                  if (!updatedIdea) return idea;
                  return { ...idea, ...updatedIdea };
                }),
              }),
            );
            // Requires full refetch to update pinned ideas which are now bubbled to top
            api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
          },
          () => {},
        );
      };
      if (
        statusKey === "status" &&
        status &&
        extra.notifyAuthors === undefined
      ) {
        const statusDef = challenge.ownerOrganisation?.ideaStatuses?.find(
          (s) => s._id === status,
        );
        if (statusDef) {
          setStatusModal({ ideaIds: selectedIdeaIds, status: statusDef });
          return;
        }
      }
      performUpdate();
    },
    [challengeId, challenge, t],
  );

  const bulkMoveIdeas = useCallback(
    (
      ideaIds: string[],
      newChallengeId: string,
      onComplete: () => void = () => {},
    ) => {
      api.ideas.moveToChallenge(
        ideaIds,
        newChallengeId,
        (data) => {
          toast.success(
            `${t("common:capitalise", { key: "generic.ideas" })} moved to ${t("generic.challenge")}`,
          );
          ideaSelection.clear();

          api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
          api.invalidateQueries(api.challenges.getIdeas, [newChallengeId]);
          for (const id of ideaIds) {
            // Because we retain placeholder data ensure that the challenge and ownerChallenge are updated immediately, but still invalidate the query to get the latest data once opened
            api.setQueryData(api.ideas.get, [id], (prev) => {
              if (!prev) {
                return prev;
              }
              prev.challenge = data.challenge;
              prev.ownerChallenge = data.ownerChallenge;
              prev.projectManagement = { boards: [{ forId: data.challenge }] };
              return prev;
            });
            api.invalidateQueries(api.ideas.get, [id]);
          }
          if (onComplete) onComplete();
        },
        (err) => toast.error(err.message),
      );
    },
    [t, challengeId, ideaSelection],
  );

  const createChallengeFromIdea = useCallback(
    (ideaId: string) => {
      api.challenges.createFromIdea(
        { idea: ideaId },
        (data) => {
          navigate(`/challenges/${data.challenge._id}`);
          toast.success("Challenge created");
        },
        (err) => toast.error(err.message),
      );
    },
    [navigate],
  );

  const removeIdeaFromMerge = useCallback(
    (
      mergeIdeaId: string,
      ideaId: string,
      onComplete = (_mergeIdeaId: string, _ideaId: string) => {},
      onError = (_mergeIdeaId: string, _ideaId: string) => {},
    ) => {
      // If you're removing the 2nd to last idea from a merge, then we delete the merge container, as there'd be no point in having it
      const mergeIdea = data.ideas.find((i) => i._id === mergeIdeaId);
      const willDeleteMergeContainer =
        mergeIdea?.children && mergeIdea.children.length <= 2;
      util
        .confirm(
          `Are you sure you want to remove this ${t("generic.idea")} from the merge?`,
          `The ${t("generic.idea")} will not be deleted and will remain in the ${t("generic.challenge")}. You can re-add it to the merge at any time. ${willDeleteMergeContainer ? `The merge container will be deleted as it will only contain one ${t("generic.idea")}.` : ""}`,
        )
        .then(() => {
          setRemovingIdeaFromMerge(ideaId);
          api.ideas.removeIdeaFromMerge(
            mergeIdeaId,
            ideaId,
            () => {
              if (willDeleteMergeContainer) {
                // If we delete the merge container, we get the ID of the idea that wasn't deleted, and change the preview ID to that
                const nonDeletedIdeaId = mergeIdea?.children?.find(
                  (c) => c !== ideaId,
                );
                if (nonDeletedIdeaId) {
                  setPreviewId(nonDeletedIdeaId);
                }
              }
              setRemovingIdeaFromMerge("");
              api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
              api.invalidateQueries(api.ideas.get, [ideaId]);
              api.invalidateQueries(api.ideas.get, [mergeIdeaId]);
              onComplete(mergeIdeaId, ideaId);
            },
            (err) => {
              toast.error(err.message);
              setRemovingIdeaFromMerge("");
              onError(mergeIdeaId, ideaId);
            },
          );
        })
        .catch(() => {});
    },
    [challengeId, data.ideas, t, setPreviewId],
  );

  const updateProjectLane = useCallback(
    (
      ideaIds: string[],
      targetLane: string,
      order: number | null = null,
      announcementIds: string[] = [],
    ) => {
      api.boards.moveProjectIdeas(
        challengeId,
        {
          targetLane,
          ideaIds,
          order: order ?? 0,
          sendAnnouncements: announcementIds,
        },
        ({ updatedIdeas }) => {
          if (ideaIds.length === 1) {
            toast.success(`Moved ${t("generic.idea")}`);
          } else {
            toast.success(`Moved ${ideaIds.length} ${t("generic.ideas")}`);
          }
          ideaSelection.clear();
          api.setQueryData(api.challenges.getIdeas, [challengeId], (prev) => ({
            ...prev,
            ideas: prev.ideas.map((idea) => {
              const updatedIdea = updatedIdeas?.find((i) => i._id === idea._id);
              if (updatedIdea) {
                return {
                  ...idea,
                  projectManagement: updatedIdea.projectManagement,
                };
              }
              return idea;
            }),
          }));
          // Refetch required for project lane impacts
          api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
          api.invalidateQueries(api.challenges.getIdeaImpacts, [challengeId]);
        },
        (err) => {
          toast.error(`Unable to move ${t("generic.ideas")}. ${err.message}`);
        },
      );
    },
    [challengeId, ideaSelection, t],
  );

  const checkUpdateProjectLane = (
    ideaIds: string[],
    laneId: string,
    order: number | null = null,
  ) => {
    const newLane = projectLaneQuery.data?.lanes.find((l) => l._id === laneId);
    if (!newLane?.announcements) {
      updateProjectLane(ideaIds, laneId, order, []);
    } else {
      setLaneAnnouncements(newLane.announcements);
      setConfirmAnnouncementModalOpen(true);
      setLaneUpdateContext({ laneId, ideaIds, order });
    }
  };

  const updateIdeaProjectDeadlineMulti = useCallback(
    (deadline: Date) => {
      const formattedDeadline = new Date(deadline);
      api.boards.updateProjectIdeasDeadline(
        challengeId,
        {
          deadline: formattedDeadline.toISOString(),
          ideaIds: selectedIdeas.map((i) => i._id!),
        },
        ({ updatedIdeas }) => {
          api.setQueryData(api.challenges.getIdeas, [challengeId], (prev) => ({
            ...prev,
            ideas: prev.ideas.map((idea) => {
              const updatedIdea = updatedIdeas.find((i) => i._id === idea._id);
              if (!updatedIdea) return idea;

              return {
                ...idea,
                projectManagement: updatedIdea.projectManagement,
              };
            }),
          }));
          toast.success(
            `Updated deadline for ${util.pluralise(updatedIdeas.length, t("generic.idea"), t("generic.ideas"), true)}`,
          );
        },
        (err) => {
          toast.error(`Unable to update deadline. ${err.message}`);
        },
      );
    },
    [challengeId, selectedIdeas, t],
  );

  const bulkProjectAssign = useCallback(
    (userIds: string[]) => {
      api.boards.bulkAssignProjectIdeas(
        challengeId,
        { ideaIds: selectedIdeas.map((i) => i._id), userIds },
        ({ updatedIdeas }) => {
          api.setQueryData(api.challenges.getIdeas, [challengeId], (prev) => ({
            ...prev,
            ideas: prev.ideas.map((idea) => {
              const updatedIdea = updatedIdeas.find((i) => i._id === idea._id);
              if (!updatedIdea) return idea;
              return {
                ...idea,
                projectManagement: updatedIdea.projectManagement,
              };
            }),
          }));
          ideaSelection.clear();
          toast.success(
            `Assigned ${util.pluralise(userIds.length, "user", "users", true)} for ${util.pluralise(updatedIdeas.length, t("generic.idea"), t("generic.ideas"), true)}`,
          );
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [challengeId, ideaSelection, selectedIdeas, t],
  );

  const [, setTrayStatus] = useTrayStatus();

  const createIdeaPdfExport = useCallback(
    (ideas: ChallengeIdeaListItem[], appendCreditsafeReport: boolean) => {
      if (!user) return;
      if (!challenge) return;
      if (ideas.length === 0) return;

      const ideaIds = ideas.map((idea) => idea._id);
      const challengeName =
        challenge?.name || t("common:capitalise", { key: "generic.challenge" });
      const displayName = `${challengeName} ${t("generic.idea")} export.pdf`;

      api.users.createDownload(
        user._id,
        {
          downloadType: "ideaPdfExport",
          displayName,
          ideas: ideaIds,
          challenge: challenge._id,
          appendCreditsafeReport,
        },
        (download) => {
          api.setQueryData(
            api.users.getDownloads,
            [user._id],
            (prev) => ({
              ...prev,
              downloads: [download, ...(prev?.downloads || [])],
            }),
            { allowUndefined: true },
          );
          setTrayStatus({ open: true, activeIndex: 1 });
        },
        () => {},
      );
    },
    [challenge, setTrayStatus, user, t],
  );

  const ideaHasCreditsafeReport = useCallback(
    (ideas: ChallengeIdeaListItem[]) => {
      if (!user) return false;
      if (!challenge) return false;
      if (
        !(
          util.canManageChallenge(user, challenge) ||
          util.hasPermission(user, "challenge.viewIdeas", challenge._id)
        )
      ) {
        return false;
      }

      const ideaTemplate =
        challenge.ownerIdeaTemplate ||
        ideas.find((idea) => idea.ownerIdeaTemplate)?.ownerIdeaTemplate ||
        (
          ideas.find((idea) => idea.ownerChallenge)?.ownerChallenge as
            | { ownerIdeaTemplate?: OpenAPI.Schemas["ideaTemplates"] }
            | undefined
        )?.ownerIdeaTemplate;

      return !!ideaTemplate?.body?.find(
        (
          section: NonNullable<
            OpenAPI.Schemas["ideaTemplates"]["body"]
          >[number],
        ) =>
          section?.fields?.find(
            (
              field: NonNullable<
                NonNullable<
                  OpenAPI.Schemas["ideaTemplates"]["body"]
                >[number]["fields"]
              >[number],
            ) => field.type === "creditsafereport",
          ),
      );
    },
    [challenge, user],
  );

  const ideaActions = useMemo<Action<ChallengeIdeaListItem>[]>(() => {
    if (!challenge) return [];

    const canShowBoardProjectActions =
      ideaViewType === "board" &&
      (canManageChallenge ||
        util.hasPermission(user, "challenge.moveProjectIdeas", challengeId) ||
        util.hasPermission(
          user,
          "challenge.editProjectAssignees",
          challengeId,
        ) ||
        util.hasPermission(user, "challenge.editProjectDates", challengeId));

    return [
      // Board-only project management bulk actions
      (items) => ({
        name: `Move all ${t("generic.ideas")} to project lane`,
        icon: MoveIcon,
        appear: "multi",
        isVisible:
          canShowBoardProjectActions &&
          (canManageChallenge ||
            util.hasPermission(
              user,
              "challenge.moveProjectIdeas",
              challengeId,
            )),
        onClick: () => setMoveLaneIdeas(items),
      }),
      (items) => ({
        name: `Project assign users to all ${t("generic.ideas")}`,
        icon: UserPlusIcon,
        appear: "multi",
        isVisible:
          canShowBoardProjectActions &&
          (canManageChallenge ||
            util.hasPermission(
              user,
              "challenge.editProjectAssignees",
              challengeId,
            )),
        onClick: () => setProjectAssignIdeas(items.map((i) => i._id)),
      }),
      (items) => ({
        name: "Set management deadline",
        icon: CalendarIcon,
        appear: "both",
        isVisible:
          canShowBoardProjectActions &&
          (canManageChallenge ||
            util.hasPermission(
              user,
              "challenge.editProjectDates",
              challengeId,
            )),
        onClick: () => setManagementDeadlineIdeas(items),
      }),

      // Remove custom submission deadline
      (items) => {
        const withDeadline = items.filter((i) => i.submissionDeadline);
        const canEditDeadlines =
          canManageChallenge ||
          util.hasPermission(user, "challenge.editIdeaDeadlines", challengeId);
        return {
          name: t("ideas.authorUpdate.removeDeadline"),
          icon: CalendarXIcon,
          appear: "both",
          isVisible: canEditDeadlines && withDeadline.length > 0,
          onClick: () => {
            const ideaIds = withDeadline.map((i) => i._id!);
            api.ideas.removeDeadline(
              ideaIds,
              ({ ideas: updatedIdeas }) => {
                toast.success(
                  `Removed custom deadline for ${util.pluralise(
                    updatedIdeas.length,
                    t("generic.idea"),
                    t("generic.ideas"),
                    true,
                  )}`,
                );
                api.setQueryData(
                  api.challenges.getIdeas,
                  [challengeId],
                  (prev) => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      ideas: prev.ideas.map((idea) => {
                        const updatedIdea = updatedIdeas.find(
                          (u) => u._id === idea._id,
                        );
                        if (!updatedIdea) return idea;
                        const next = { ...idea, ...updatedIdea };
                        delete next.submissionDeadline;
                        return next;
                      }),
                    };
                  },
                );
                for (const updated of updatedIdeas) {
                  if (updated._id) {
                    api.setQueryData(api.ideas.get, [updated._id], (prev) => {
                      if (!prev) return prev;
                      const next = { ...prev, ...updated };
                      delete next.submissionDeadline;
                      return next;
                    });
                  }
                }
              },
              (err) => toast.error(err.message),
            );
          },
        };
      },

      // Pin
      (items) => ({
        name:
          items.length === 1 ? (items[0]?.isPinned ? "Unpin" : "Pin") : "Pin",
        icon: PinIcon,
        appear: "both",
        isVisible:
          canManageChallenge ||
          util.hasPermission(user, "challenge.pinIdeas", challengeId),
        tooltip: <PinIdeaHelper challenge={challenge} />,
        onClick: () =>
          bulkUpdateStatus(
            items.map((i) => i._id),
            "isPinned",
            !items.every((i) => i.isPinned),
          ),
      }),

      // Update status
      (items) => {
        const canEditStatuses =
          canManageChallenge ||
          util.hasPermission(
            user,
            "challenge.updateIdeaPublicStatus",
            challengeId,
          );

        if (!canEditStatuses || !challenge.ownerOrganisation?.ideaStatuses) {
          return {
            name: t("ideas.status.manage.setStatus"),
            icon: ListChecksIcon,
            isVisible: false,
          };
        }

        const statusIds = items.map(
          (i) =>
            i.ideaStatus?.status ??
            i.ownerStatus?._id ??
            (i as { status?: string }).status,
        );
        const allSame =
          statusIds.length > 0 &&
          statusIds.every((id) => id && id === statusIds[0]);
        const currentStatusDef = allSame
          ? challenge.ownerOrganisation.ideaStatuses.find(
              (s) => s._id === statusIds[0],
            )
          : null;

        return {
          name: t("ideas.status.manage.setStatus"),
          icon: ListChecksIcon,
          appear: "both",
          actions: [
            ...(challenge.ownerOrganisation?.ideaStatuses.map((status) => ({
              name: (
                <Label size="small" color={status.color}>
                  {status.name}
                </Label>
              ),
              onClick: () => {
                bulkUpdateStatus(
                  items.map((i) => i._id!),
                  "status",
                  status._id,
                );
              },
            })) ?? []),
            { type: "Divider" as const },
            {
              name: t("ideas.authorUpdate.otherUpdate"),
              onClick: () => {
                setStatusModal({
                  ideaIds: items.map((i) => i._id!),
                  status: currentStatusDef ?? null,
                });
              },
            },
          ],
        };
      },

      // Groups
      {
        name: t("ideas.actions.addAuthorsToGroup"),
        icon: UsersIcon,
        appear: "both",
        checkPermission: "org.addToGroupsDirectly",
        checkPermissionScope: user?.ownerOrganisation?._id,
        onClick: (items) => setGroupAddIdeas(items),
      },

      // Next phase challenge
      {
        name: `Move to next-phase`,
        appear: "multi",
        icon: CornerLeftUpIcon,
        isVisible:
          canManageChallenge ||
          util.hasPermission(user, "challenge.editSettings", challengeId),
        onClick: (items) => setNextPhaseChallenge(items.map((i) => i._id)),
      },

      // Merge
      (items) => {
        const canMerge =
          items.length > 1 &&
          (items.filter((i) => i.parent || i.children).length === 0 ||
            (items.filter((i) => i.children).length === 1 &&
              items.filter((i) => !(i.parent || i.children)).length ===
                items.length - 1));

        return {
          name: "Merge",
          icon: NetworkIcon,
          appear: "multi",
          isVisible:
            ideaMergeEnabled &&
            (canManageChallenge ||
              util.hasPermission(user, "challenge.mergeIdeas", challengeId)),
          disabled: items.length < 2 || !canMerge,
          tooltip: canMerge
            ? `Merge ${t("generic.ideas")} together`
            : items.length > 1
              ? `You can only merge ${t("generic.ideas")} where none of the previous ${t("generic.ideas")} have been merged`
              : `Select at least two ${t("generic.ideas")} to merge`,
          onClick: () => {
            // @ts-ignore
            confirmMerge(items, projectLaneQuery.data?.lanes, challenge)
              .then(() => {
                ideaSelection.clear();
                api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
                api.invalidateQueries(api.ideas.get, []);
              })
              .catch(() => {});
          },
        };
      },

      // Assign assessors
      (items) => {
        const canAssign =
          challenge.ownerIdeaTemplate?.assessmentEnabled &&
          (canManageChallenge ||
            util.hasPermission(
              user,
              "challenge.editAssessments",
              challengeId,
            )) &&
          assessmentRounds.length > 0;

        if (!canAssign) {
          return {
            name: "Assign assessors",
            icon: UserPlusIcon,
            isVisible: false,
          };
        }

        if (assessmentRounds.length > 1) {
          return {
            name: "Assign assessors",
            icon: UserPlusIcon,
            appear: "both",
            actions: assessmentRounds.map((round) => ({
              name: `Assign to ${round.name}`,
              onClick: () => {
                setAssessmentAssignRoundId(round.id);
                setAssessmentAssignIdeas(items.map((i) => i._id!));
              },
            })),
          };
        }

        const firstRound = assessmentRounds[0];
        if (!firstRound) {
          return {
            name: "Assign assessors",
            icon: UserPlusIcon,
            isVisible: false,
          };
        }

        return {
          name: "Assign assessors",
          icon: UserPlusIcon,
          appear: "both",
          onClick: () => {
            setAssessmentAssignRoundId(firstRound.id);
            setAssessmentAssignIdeas(items.map((i) => i._id!));
          },
        };
      },

      // Move to another challenge / create from idea (non-board only)
      (items) => ({
        name: `Move to another ${t("generic.challenge")}`,
        icon: ArrowRightIcon,
        appear: "both",
        isVisible:
          ideaViewType !== "board" &&
          (canManageChallenge ||
            util.hasPermission(
              user,
              "challenge.moveIdeasToChallenge",
              challengeId,
            )),
        onClick: () => setChallengeChooserIdea(items.map((i) => i._id)),
      }),
      (items) => ({
        name: `Create ${t("generic.challenge")} from ${t("generic.idea")}`,
        icon: TargetIcon,
        appear: "single",
        isVisible:
          ideaViewType !== "board" &&
          !!items[0] &&
          "challengeCreation" in items[0] &&
          !!items[0].challengeCreation &&
          (canManageChallenge ||
            util.hasPermission(
              user,
              "challenge.moveIdeasToChallenge",
              challengeId,
            )),
        onClick: () => {
          const firstItem = items[0];
          if (firstItem?._id) {
            createChallengeFromIdea(firstItem._id);
          }
        },
      }),

      // PDF export
      (items) => {
        const hasCreditsafe = ideaHasCreditsafeReport(items);
        if (hasCreditsafe) {
          return {
            name: "Export as PDF",
            icon: FileTextIcon,
            appear: "both",
            actions: [
              {
                name: "Regular PDF",
                onClick: () => createIdeaPdfExport(items, false),
              },
              {
                name: "With creditsafe Report",
                onClick: () => createIdeaPdfExport(items, true),
              },
            ],
          };
        }
        return {
          name: "Export as PDF",
          icon: FileTextIcon,
          appear: "both",
          onClick: () => createIdeaPdfExport(items, false),
        };
      },

      // Label
      {
        name: "Apply a label",
        icon: BookmarkIcon,
        appear: "both",
        isVisible:
          canManageChallenge ||
          util.hasPermission(user, "challenge.editIdeaLabels", challengeId),
        onClick: (items) => setLabelIdeas(items),
      },

      // Bulk selection convenience
      {
        name: "Deselect all",
        icon: BanIcon,
        appear: "multi",
        onClick: () => ideaSelection.clear(),
      },

      // Delete
      (items) => ({
        name:
          items.length === 1 && !!items[0]?.children
            ? `Delete merged ${t("generic.idea")}`
            : `Delete ${t("generic.idea")}${items.length === 1 ? "" : "s"}`,
        icon: Trash2Icon,
        appear: "both",
        isVisible:
          canManageChallenge ||
          util.hasPermission(user, "challenge.deleteIdeas", challengeId),
        tooltip:
          items.length === 1 && !!items[0]?.children
            ? `Delete this merged ${t("generic.idea")}. None of the contained ${t("generic.ideas")} will be deleted, and they will be returned to the ${t("generic.idea")} list.`
            : null,
        onClick: () => setDeletingIdeas(items),
      }),
    ];
  }, [
    assessmentRounds,
    canManageChallenge,
    challenge,
    challengeId,
    createChallengeFromIdea,
    createIdeaPdfExport,
    bulkUpdateStatus,
    ideaHasCreditsafeReport,
    ideaMergeEnabled,
    ideaViewType,
    projectLaneQuery.data?.lanes,
    ideaSelection,
    t,
    user,
  ]);

  const voteLimit = Number(challenge.voteLimit ?? 0);
  const usedVotes = voteLimit === data.ideasVotedFor;
  const votesRemaining = voteLimit - data.ideasVotedFor;

  const activeIdeaViewType = useMemo(
    () => IdeaViewTypes.find((viewType) => viewType.key === ideaViewType),
    [ideaViewType],
  );

  const searchComponent = useMemo(
    () => (
      <Input
        size="small"
        placeholder={
          canManageChallenge || canViewProjectBoard
            ? `${t("challenge.ideas.filter.admin.search")}...`
            : `${t("challenge.ideas.filter.search")}...`
        }

        value={ideaFilters.query}
        onChange={(e) => updateSearchTerm(e.target.value)}
        style={{ maxWidth: theme.sizes.isMobile ? undefined : 350 }}
        fluid
      />
    ),
    [
      canManageChallenge,
      canViewProjectBoard,
      ideaFilters.query,
      theme.sizes.isMobile,
      updateSearchTerm,
      t,
    ],
  );

  const movePreviewByDirection = useCallback(
    (direction: "prev" | "next") => {
      movePreviewIdea(direction === "prev" ? -1 : 1);
    },
    [movePreviewIdea],
  );

  const openPreviewId = useCallback<Dispatch<SetStateAction<string>>>(
    (ideaIdOrUpdater) => {
      const ideaId =
        typeof ideaIdOrUpdater === "function"
          ? ideaIdOrUpdater("")
          : ideaIdOrUpdater;
      if (ideaId) {
        setPreviewId(ideaId);
      }
    },
    [setPreviewId],
  );

  const groupUserIds = useMemo(
    () =>
      Array.from(
        new Set(
          groupAddIdeas.flatMap((idea) => {
            const userIds: string[] = [];
            if (idea.user) {
              userIds.push(idea.user);
            }
            if (idea.collaborators) {
              userIds.push(...idea.collaborators);
            }
            return userIds;
          }),
        ),
      ),
    [groupAddIdeas],
  );

  const existingLabelIds = useMemo(
    () => Array.from(new Set(labelIdeas.flatMap((i) => i.labels || []))),
    [labelIdeas],
  );

  return (
    <StyledIdeasContainer>
      <ConfirmAnnouncementModal
        onClose={() => setConfirmAnnouncementModalOpen(false)}
        modalOpen={confirmAnnouncementModalOpen}
        ideaCount={
          laneUpdateContext.ideaIds ? laneUpdateContext.ideaIds.length : 0
        }
        announcements={laneAnnouncements}
        handleConfirm={(announcementIds) => {
          setConfirmAnnouncementModalOpen(false);

          updateProjectLane(
            laneUpdateContext.ideaIds ?? [],
            laneUpdateContext.laneId ?? "",
            laneUpdateContext.order,
            announcementIds,
          );

          setLaneUpdateContext({});
        }}
      />

      <ChallengeIdeaFilters
        // @ts-ignore
        challenge={challenge}
        popularTags={ideaFilterQuery.data?.popularTags}
        allLabels={ideaFilterQuery.data?.allLabels}
        availableGroups={ideaFilterQuery.data?.availableGroups}
        availableOrganisations={ideaFilterQuery.data?.availableOrganisations}
        availableSortParameters={ideaFilterQuery.data?.availableSortParameters}
        availableFilterParameters={
          ideaFilterQuery.data?.availableFilterParameters
        }
        allProjectAssignees={
          ideaFilterQuery.data?.allProjectAssignees as
            | OpenAPI.Schemas["User"][]
            | undefined
        }
        availableImpacts={ideaFilterQuery.data?.availableImpacts}
      />

      <IdeaDeletionModal
        challengeId={challengeId}
        ideasToDelete={deletingIdeas}
        setIdeasToDelete={setDeletingIdeas}
        deselectIdeas={ideaSelection.deselect}
      />
      <ManageIdeaStatusModal
        open={Boolean(statusModal)}
        ideaId={statusModal?.ideaIds[0] ?? ""}
        ideaIds={statusModal?.ideaIds ?? []}
        status={statusModal?.status ?? null}
        statuses={challenge.ownerOrganisation?.ideaStatuses ?? []}
        ideaTemplate={challenge.ownerIdeaTemplate}
        challengeId={challengeId}
        ideas={(statusModal?.ideaIds ?? [])
          .map((id) => paginatedIdeas.find((i) => i._id === id))
          .filter((i): i is ChallengeIdeaListItem => !!i)}
        onClose={() => setStatusModal(null)}
        onUpdated={(updatedIdeas) => {
          toast(
            `${statusModal?.ideaIds.length ?? 0} ${t("generic.ideas")} updated`,
          );
          const withDualStatus = (updated: (typeof updatedIdeas)[number]) => {
            const ideaStatus = updated.ideaStatus;
            return {
              ...updated,
              ownerStatus: ideaStatus?.ownerStatus,
              statusMessage: ideaStatus?.message,
              statusAssignedAt: ideaStatus?.assignedAt,
              status: ideaStatus?.status,
            };
          };
          api.setQueryData(api.challenges.getIdeas, [challengeId], (prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              ideas: prev.ideas.map((idea) => {
                const updatedIdea = updatedIdeas.find(
                  (u) => u._id === idea._id,
                );
                return updatedIdea
                  ? { ...idea, ...withDualStatus(updatedIdea) }
                  : idea;
              }),
            };
          });
          for (const updated of updatedIdeas) {
            if (updated._id) {
              api.setQueryData(api.ideas.get, [updated._id], (prev) =>
                prev ? { ...prev, ...withDualStatus(updated) } : prev,
              );
            }
          }
          api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
          setStatusModal(null);
        }}
      />
      <IdeaManagementDeadlineModal
        managementDeadlineIdeas={managementDeadlineIdeas}
        setManagementDeadlineIdeas={setManagementDeadlineIdeas}
        updateIdeaProjectDeadlineMulti={updateIdeaProjectDeadlineMulti}
      />
      <IdeaMoveToLaneModal
        moveLaneIdeas={moveLaneIdeas}
        setMoveLaneIdeas={setMoveLaneIdeas}
        allChallengeProjectLanes={projectLaneQuery.data?.lanes ?? []}
        checkUpdateProjectLane={checkUpdateProjectLane}
        challenge={challenge}
      />
      <GroupUserManagementModal
        users={groupUserIds}
        onClose={() => setGroupAddIdeas([])}
      />
      <HandledIdeaPreview
        totalIdeas={
          ideaViewType === "board" ? paginatedIdeas.length : data.total
        }
        currentIdeaPosition={
          paginatedIdeas.findIndex((i) => i._id === previewId) + 1
        }
        ideaId={previewId}
        assessmentEnabled={challenge.ownerIdeaTemplate?.assessmentEnabled}
        onCloseModal={() => {
          setPreviewId("");
        }}
        page={ideaFilters.page}
        limit={ideaFilters.limit}
        moveIdea={
          currentPreviewIdInList || crossPageNavigating
            ? movePreviewByDirection
            : undefined
        }
        canGoNextIdea={canGoNextIdea}
        canGoPrevIdea={canGoPrevIdea}
        removeIdeaFromMerge={removeIdeaFromMerge}
        removingIdeaFromMerge={!!removingIdeaFromMerge}
        challenge={challenge}
        updateProjectLane={checkUpdateProjectLane}
      />
      <LabelChooser
        isOpen={labelIdeas.length > 0}
        onClose={() => setLabelIdeas([])}
        challengeId={challengeId}
        existing={existingLabelIds}
        onComplete={(label) =>
          api.challenges.addIdeasLabels(
            challengeId,
            { ideas: labelIdeas.map((i) => i._id!), labels: [label] },
            ({ ideas: updatedIdeas }) => {
              api.setQueryData(
                api.challenges.getIdeas,
                [challengeId],
                (prev) => ({
                  ...prev,
                  ideas: prev.ideas.map((i) => {
                    const updatedIdea = updatedIdeas?.find(
                      (u) => u._id === i._id,
                    );
                    if (!updatedIdea) return i;
                    return { ...i, ...updatedIdea };
                  }),
                }),
              );
              setLabelIdeas((prevIdeas) =>
                prevIdeas.map((idea) => ({
                  ...idea,
                  labels: [
                    ...(idea.labels || []).filter((l) => l !== label),
                    label,
                  ],
                })),
              );
            },
            () => {},
          )
        }
        onRemoveExisting={(label) => {
          api.challenges.removeIdeasLabels(
            challengeId,
            { ideas: labelIdeas.map((i) => i._id!), labels: [label] },
            ({ ideas: updatedIdeas }) => {
              api.setQueryData(
                api.challenges.getIdeas,
                [challengeId],
                (prev) => ({
                  ...prev,
                  ideas: prev.ideas.map((i) => {
                    const updatedIdea = updatedIdeas?.find(
                      (u) => u._id === i._id,
                    );
                    if (!updatedIdea) return i;
                    return { ...i, ...updatedIdea };
                  }),
                }),
              );
              setLabelIdeas((prevIdeas) =>
                prevIdeas.map((idea) => ({
                  ...idea,
                  labels: (idea.labels || []).filter((l) => l !== label),
                })),
              );
            },
            () => {},
          );
        }}
      />
      {challenge && (
        <GatedChallengeModal
          openModal={nextPhaseChallenge.length > 0}
          onCloseModal={() => setNextPhaseChallenge([])}
          challengeName={challenge.name}
          challengeId={challenge._id}
          newGroupUsers={selectedIdeas.flatMap((idea) =>
            idea.collaborators
              ? [idea.user].concat(idea.collaborators)
              : [idea.user],
          )}
        />
      )}
      <UserChooser
        isOpen={projectAssignIdeas.length > 0}
        onClose={() => setProjectAssignIdeas([])}
        onComplete={(users) => bulkProjectAssign(users.map((u) => u._id!))}
        forType="projectAssignee"
        forId={challengeId}
        clearOnComplete
        enabledFeatures={{ search: true, invite: false }}
        subtitle={`Choose multiple users to project assign to ${t("generic.idea")}.`}
        confirm="Assign these users"
        searchFunction={(term, cb, fail) =>
          api.boards.searchProjectAssignees(
            challengeId,
            term,
            ({ users }) => cb(users),
            fail,
          )
        }
      />
      <UserChooser
        isOpen={assessmentAssignIdeas.length > 0}
        onClose={() => {
          setAssessmentAssignRoundId("");
          setAssessmentAssignIdeas([]);
        }}
        onComplete={(users) => {
          // @ts-ignore
          const emailUsers = users.filter((u) => u.isEmailInvitee);
          // @ts-ignore
          const existingUsers = users.filter((u) => !u.isEmailInvitee);
          if (emailUsers.length) {
            const ideaDetails: Record<string, { assessmentRounds: string[] }> =
              {};
            for (const ideaId of assessmentAssignIdeas) {
              ideaDetails[ideaId] = {
                assessmentRounds: [assessmentAssignRoundId],
              };
            }

            api.invitations.createBulk(
              {
                invitees: emailUsers.map((u) => u._id),
                invitationType: "email",
                forType: "ideaAssessor",
                forId: challengeId,
                forIdeas: assessmentAssignIdeas,
                ideaDetails,
              },
              () => {
                api.invalidateQueries(api.invitations.getForType, [
                  "ideaAssessor",
                  challengeId,
                ]);
                toast.success(
                  `Invitation${emailUsers.length > 1 ? "s" : ""} sent`,
                );
              },
              () => {},
            );
            api.setQueryData(
              api.challenges.getIdeas,
              [challengeId],
              (prev) => ({
                ...prev,
                ideas: prev.ideas.map((idea) => {
                  if (assessmentAssignIdeas.indexOf(idea._id) === -1)
                    return idea;
                  return {
                    ...idea,
                    invitedExternalAssessors: [
                      ...(idea.invitedExternalAssessors || []),
                      ...emailUsers.map((u) => u._id),
                    ],
                  };
                }),
              }),
            );
          }

          const filteredExistingUsers = existingUsers.filter(
            (u) => !u.isEmailInvitee,
          );
          if (!filteredExistingUsers.length) return;

          api.challenges.assignAssessors(
            challengeId,
            assessmentAssignIdeas,
            filteredExistingUsers.map((u) => u._id),
            assessmentAssignRoundId,
            ({ ideas: assessorIdeas }) => {
              api.setQueryData(
                api.challenges.getIdeas,
                [challengeId],
                (prev) => ({
                  ...prev,
                  ideas: prev.ideas.map((idea) => {
                    const updatedIdea = assessorIdeas.find(
                      (i) => i._id === idea._id,
                    );
                    if (!updatedIdea) return idea;
                    return {
                      ...idea,
                      assessments: updatedIdea.assessments,
                      assessmentDetails: updatedIdea.assessmentDetails,
                    };
                  }),
                }),
              );
              toast.success("Assessors assigned");
            },
            (err) => toast.error(err.message),
          );
        }}
        forType="assessor"
        forId={challengeId}
        clearOnComplete
        enabledFeatures={{ search: true, invite: true }}
        subtitle={`Choose users to assign as assessors for ${assessmentAssignIdeas.length > 1 ? `the selected ${t("generic.ideas")}` : `the selected ${t("generic.idea")}`}. Assessors will be able to find the ${t("generic.ideas")} on their 'Assessments' tab within this ${t("generic.challenge")}.`}
        confirm="Assign these assessors"
        beforeContent={() => {
          const assessmentRound = assessmentRounds.find(
            (round) => round.id === assessmentAssignRoundId,
          );
          if (!assessmentRound) return null;
          const assessmentClosed = assessmentRound.assessmentClosed || false;
          if (!assessmentClosed) return null;

          return (
            <Message color="orange">
              <MessageHeader>Assessment closed</MessageHeader>
              <MessageContent>
                Assessment is now closed for this {t("generic.challenge")}. If
                you assign assessors, they will not be able to complete their
                assessment. You can change this{" "}
                <Link to={`/challenges/${challenge._id}/settings/format`}>
                  here
                </Link>
                .
              </MessageContent>
            </Message>
          );
        }}
        searchFunction={(term, cb, fail) =>
          api.search.challengeAssessors(
            challengeId,
            term,
            ({ users }) => cb(users),
            fail,
          )
        }
        trigger={undefined}
      />
      {challenge._id && challengeChooserIdea?.length > 0 ? (
        <ChallengeChooser
          isOpen={challengeChooserIdea.length > 0}
          onClose={() => setChallengeChooserIdea([])}
          single
          subtitle={`Find the challenge you want to move the ${util.pluralise(challengeChooserIdea?.length, t("generic.idea"), t("generic.ideas"), false)} to.`}
          ignores={[challenge._id]}
          onComplete={(c) => {
            const target = c[0];
            if (target?._id) {
              bulkMoveIdeas(challengeChooserIdea, target._id, () =>
                setChallengeChooserIdea([]),
              );
            }
          }}
          beforeContent={`We'll move the selected ${t("generic.idea")}(s) to another ${t("generic.challenge")} that you can manage.`}
          afterContent={
            <Message color="orange">
              <p>
                <Icon icon={TriangleAlertIcon} />
                <b>Caution: </b>
                {`Moving ${t("generic.ideas")} to another ${t("generic.challenge")} will have the following side effects.`}
              </p>
              <List variant="bulleted" compact>
                <ListItem>
                  The {t("generic.idea")} will be removed from the original{" "}
                  {t("generic.challenge")}
                </ListItem>
                <ListItem>
                  The {t("generic.idea")} will keep the {t("generic.idea")}{" "}
                  format of the original {t("generic.challenge")}
                </ListItem>
                <ListItem>
                  {t("common:capitalise", { key: "generic.idea" })}{" "}
                  collaborators will be removed
                </ListItem>
                <ListItem>Assessments will be removed</ListItem>
                <ListItem>Project board assignees will be removed</ListItem>
                <ListItem>Project board notes will be removed</ListItem>
              </List>
            </Message>
          }
        />
      ) : null}
      {ideaViewType !== "board" ? (
        <ChallengeMyIdeas
          challengeId={challenge._id}
          style={{ marginBottom: 20 }}
          showMyIdeas={showMyIdeas}
          setShowMyIdeas={setShowMyIdeas}
        />
      ) : null}
      {user ? (
        <div style={{ marginBottom: theme.sizes.isMobile ? 0 : 10 }}>
          <FilterContainer>
            <StyledFilters style={{ flex: 1 }}>
              {ideaViewType !== "board" ? (
                challenge.voteLimit &&
                challenge.votingVisibility === "users" ? (
                  <Label>
                    {!usedVotes && <Icon icon={ThumbsUpIcon} />}{" "}
                    {util.pluralise(votesRemaining, "like", "likes")} remaining
                  </Label>
                ) : null
              ) : null}
              <ChallengeIdeaFiltersButton
                label={
                  theme.sizes.isLargeComputer
                    ? undefined
                    : t("ideas.filters.filtersButton")
                }
              />

              {theme.sizes.isMobile ? null : searchComponent}

              {canManageChallenge || canActionIdeas ? (
                ideaViewType === "board" ? (
                  <Button
                    size="small"
                    onClick={() => {
                      if (selectingMultipleCards) {
                        ideaSelection.clear();
                      }
                      setSelectingMultipleCards((prev) => !prev);
                    }}
                  >
                    <Icon icon={GripIcon} />
                    {selectingMultipleCards
                      ? "Exit selection mode"
                      : "Select multiple cards"}
                  </Button>
                ) : (
                  <Button
                    size="small"
                    onClick={() => ideaSelection.select(data.ideas)}
                    variant="outline"
                  >
                    <Icon icon={CheckIcon} />
                    {t("generic.selectAll")}
                  </Button>
                )
              ) : null}

              {basicIdeaQuery.isLoading ? <Loader /> : null}
              {selectedIdeas && selectedIdeas.length > 0 && user && (
                <ActionDropdown
                  actions={ideaActions}
                  items={selectedIdeas}
                  type="multi"
                  user={user}
                  trigger={
                    <Button size="small" color="primary">
                      <Icon icon={ChevronDownIcon} />
                      {util.pluralise(
                        selectedIdeas.length,
                        t("generic.idea"),
                        t("generic.ideas"),
                      )}
                    </Button>
                  }
                />
              )}
              {ideaViewType === "board" ? (
                canManageChallenge ||
                util.hasPermission(
                  user,
                  "challenge.viewProjectBoard",
                  challenge._id,
                ) ? (
                  <BoardEvents
                    forId={challengeId}
                    setCurrentIdeaId={openPreviewId}
                    buttonProps={{ size: "small" }}
                  />
                ) : null
              ) : null}
            </StyledFilters>

            <StyledFilters>
              <Popup
                on="hover"
                trigger={
                  <Dropdown
                    direction="left"
                    trigger={
                      <Button
                        size="small"
                        variant="outline"
                        style={{ flexWrap: "nowrap", display: "flex" }}
                      >
                        <Icon icon={activeIdeaViewType?.icon} />
                        {activeIdeaViewType?.text}
                      </Button>
                    }
                    onChange={(_e, { value }) =>
                      setIdeaViewType(
                        value as ChallengeIdeaFiltersState["ideaViewType"],
                      )
                    }
                  >
                    <DropdownMenu>
                      {IdeaViewTypes.filter((viewType) => {
                        if (viewType.key === "board") {
                          return (
                            challenge.projectManagementVisibility === "users" ||
                            canManageChallenge ||
                            util.hasPermission(
                              user,
                              "challenge.viewProjectBoard",
                              challengeId,
                            )
                          );
                        }
                        return true;
                      }).map(({ key, text, value, icon }) => (
                        <DropdownItem key={key} value={value}>
                          <Icon icon={icon} />
                          {`${text} view`}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                }
              >{`Change ${t("generic.idea")} view`}</Popup>
            </StyledFilters>
          </FilterContainer>
          {ideaViewType === "board" &&
          ideaFilters.status === "submitted" &&
          theme.sizes.isComputer ? (
            <span style={{ color: "#666", fontSize: 12 }}>
              Draft {t("generic.ideas")} are hidden by default in board view.
              You can change this in the {t("generic.idea")} filters.
            </span>
          ) : null}
          {theme.sizes.isMobile ? (
            <div style={{ marginBottom: 5, width: "100%" }}>
              {searchComponent}
            </div>
          ) : null}
        </div>
      ) : null}
      {!user && (
        <Message>
          <MessageHeader>{t("challenge.ideas.loginRequired")}</MessageHeader>
          <MessageContent>
            {t("challenge.ideas.loginRequiredDescription")}
          </MessageContent>
          <MessageActions>
            <ButtonLink
              color="primary"
              to={`/login?then=/challenges/${challenge._id}/ideas`}
            >
              {t("entry.login.loginOrSignUp")}
            </ButtonLink>
          </MessageActions>
        </Message>
      )}
      {user &&
      (challenge.ideaVisibility === "users" ||
        canManageChallenge ||
        util.hasPermission(user, "challenge.viewIdeas", challengeId) ||
        (util.hasPermission(user, "challenge.viewProjectBoard", challengeId) &&
          ideaViewType === "board")) ? (
        <StyledIdeaItemsContainer>
          {ideaViewType === "list" ? (
            <div>
              <IdeaTableView
                ideas={data.ideas as LibIdeaViewIdea[]}
                challenge={challenge}
                loading={ideasLoading}
                selection={ideaSelection}
                ideasVotedFor={data.ideasVotedFor || 0}
                ideaActions={ideaActions}
                setPreviewOpen={setPreviewId}
              />
            </div>
          ) : null}
          {ideaViewType === "card" && data.ideas.length > 0 ? (
            <Grid
              columns={4}
              tablet={2}
              mobile={1}
              style={{ marginBottom: 20, zIndex: 5 }}
            >
              {data.ideas.map((idea) => (
                <GridColumn key={idea._id}>
                  <IdeaCardView
                    idea={idea as LibIdeaViewIdea}
                    ideasVotedFor={data.ideasVotedFor || 0}
                    challenge={challenge}
                    onSelect={onSelectIdea}
                    selectedIdeas={selectedIdeas.map((i) => i._id!)}
                    setPreviewOpen={setPreviewId}
                  />
                </GridColumn>
              ))}
            </Grid>
          ) : null}
          {ideaViewType === "board" ? (
            <ProjectBoard
              forId={challengeId!}
              ideas={data.ideas!}
              ideasLoading={ideasLoading}
              defaultLane={challenge.projectBoard?.defaultLane}
              projectManagementVisibility={
                challenge.projectManagementVisibility
              }
              setPreviewId={openPreviewId}
              isSelecting={selectingMultipleCards}
              selection={ideaSelection}
              laneFilter={ideaFilters.lanes}
              checkUpdateProjectLane={checkUpdateProjectLane}
            />
          ) : null}
          {!basicIdeaQuery.isFetching &&
          data.ideas.length === 0 &&
          ideaViewType !== "board" ? (
            <EmptyBox>
              <H2>{t("challenge.ideas.noneTitle")}</H2>
              {challenge.stage === "published" && canMakeIdea && (
                <Button onClick={() => newIdea()} color="primary">
                  <Icon icon={PlusIcon} />
                  {challenge.buttonText
                    ? challenge.buttonText
                    : t("challenge.ideas.createYourIdea")}
                </Button>
              )}
            </EmptyBox>
          ) : null}
          {!basicIdeaQuery.isFetching &&
          data.ideas.length > 0 &&
          ideaViewType !== "board" ? (
            <Pagination
              limit={ideaFilters.limit}
              limitValues={
                ideaViewType === "list" ? [15, 30, 50, 100] : [16, 32, 52, 100]
              }
              page={ideaFilters.page}
              total={data.total}
              updateFilters={updateFilters}
            />
          ) : null}
        </StyledIdeaItemsContainer>
      ) : null}
    </StyledIdeasContainer>
  );
};

export default Ideas;
